// src/components/IncomingOrders/DebouncedTextField.jsx

import { debounce } from "lodash";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { TextField } from "@mui/material";

/**
 * DebouncedTextField
 *
 * A reusable MUI TextField that:
 * - Maintains local state to avoid losing focus while typing.
 * - Debounces onChange events to reduce unnecessary updates.
 * - Supports dirty-state styling when the value is modified.
 * - Can force dirty state via `forceDirty`.
 * - Flushes pending changes on Enter or blur.
 * - Resets to the original value on Escape key.
 *
 * @param {Object} props
 * @param {string} props.value - Controlled value of the field.
 * @param {(value: string) => void} props.onDebouncedChange - Callback fired after debounceDelay ms.
 * @param {number} [props.debounceDelay=300] - Delay in milliseconds before calling onDebouncedChange.
 * @param {import("@mui/material").SxProps} [props.dirtyStyle={}] - MUI sx-style applied when field is dirty.
 * @param {boolean} [props.forceDirty=false] - If true, field is marked dirty regardless of local changes.
 * @param {Object} [props.muiProps={}] - Additional props passed directly to MUI TextField.
 */
const DebouncedTextField = React.memo(function DebouncedTextField({
  value,
  onDebouncedChange,
  debounceDelay = 300,
  dirtyStyle = {},
  forceDirty = false,
  muiProps = {},
}) {
  const [localValue, setLocalValue] = useState(value || "");

  /**
   * Reset the local value to match the controlled value.
   * Used on Escape key and when props.value changes.
   */
  const resetLocalValue = () => {
    const normalized = value || "";
    setLocalValue((prev) => (prev !== normalized ? normalized : prev));
  };

  // Sync local state with prop changes
  useEffect(() => {
    resetLocalValue();
  }, [value]);

  // Debounced change callback
  const debouncedChange = useMemo(() => {
    const fn = debounce((val) => {
      onDebouncedChange?.(val);
    }, debounceDelay);
    return fn;
  }, [onDebouncedChange, debounceDelay]);

  useEffect(() => {
    return () => debouncedChange.cancel();
  }, [debouncedChange]);

  const isDirty = forceDirty || localValue !== (value || "");

  const { sx: muiSx, ...textFieldProps } = muiProps;

  return (
    <TextField
      {...textFieldProps}
      value={localValue}
      onChange={(e) => {
        const val = e.target.value;
        setLocalValue(val);
        debouncedChange(val);
      }}
      onBlur={(e) => {
        debouncedChange.flush();
        textFieldProps.onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") debouncedChange.flush();
        if (e.key === "Escape") resetLocalValue();
        textFieldProps.onKeyDown?.(e);
      }}
      sx={{
        ...muiSx,
        ...(isDirty ? dirtyStyle : {}),
      }}
    />
  );
});

DebouncedTextField.propTypes = {
  value: PropTypes.string,
  onDebouncedChange: PropTypes.func,
  debounceDelay: PropTypes.number,
  dirtyStyle: PropTypes.object,
  forceDirty: PropTypes.bool,
  muiProps: PropTypes.object,
};

DebouncedTextField.displayName = "DebouncedTextField";

export default DebouncedTextField;
